import React from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { themeGet } from 'styled-system';
import { useTheme } from 'emotion-theming';
import { useSelector } from 'react-redux';
import { Flex, Box, Heading, StarRating, Text, Icon } from 'roo-ui/components';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import find from 'lodash/find';
import Image from 'components/Image';
import TripAdvisorRating from 'components/TripAdvisorRating';
import Inclusions from 'pages/ListSearch/Inclusions';
import Currency from 'components/Currency';
import PriceBeforeDiscount from 'components/PriceBeforeDiscount';
import Truncate from 'components/Truncate';
import PointsEarnSummary from 'components/PointsEarnSummary';
import PromotionalSash from 'components/PromotionalSash';
import { mediaQuery } from 'lib/styledSystem';
import CampaignPriceMessage from 'components/CampaignPriceMessage';
import { getQueryCheckIn, getQueryCheckOut } from 'store/search/searchSelectors/querySelectors';
import CancellationRefundSummary from 'components/CancellationRefundSummary/CancellationRefundSummary';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { getNumberOfNights } from 'lib/date/numberOfNights';
import SearchResultLink from './SearchResultLink';
import { getCountry } from 'store/search/searchSelectors';

const PromotionalSashContainer = styled(Box)`
  margin-top: -${themeGet('space.3')};
  ${mediaQuery.minWidth.sm} {
    margin-top: 0;
    position: absolute;
    top: 0;
    right: ${themeGet('space.3')};
  }
`;

const ResultDetailsContainer = styled(Flex)`
  padding: ${themeGet('space.3')} ${themeGet('space.1')} ${themeGet('space.0')} ${themeGet('space.3')};
  flex-direction: column;
  flex-basis: auto;

  ${mediaQuery.minWidth.sm} {
    padding: ${themeGet('space.8')} ${themeGet('space.1')} ${themeGet('space.5')} ${themeGet('space.5')};
    flex-basis: 300px;
  }

  ${mediaQuery.minWidth.md} {
    flex-basis: 350px;
  }
`;

const getTripAdvisorRating = (customerRatings) => {
  if (!isArray(customerRatings)) return null;
  return find(customerRatings, (rating) => rating.source === 'trip_advisor');
};

const SearchResultDetail = React.memo(({ result, cardIndex }) => {
  const theme = useTheme();
  const { isLessThanBreakpoint } = useBreakpoints();
  const isMobile = isLessThanBreakpoint(0);
  const checkIn = useSelector(getQueryCheckIn);
  const checkOut = useSelector(getQueryCheckOut);
  const country = useSelector(getCountry);

  const { property, offer } = result;
  const { inclusions, charges, cancellationPolicy } = offer;

  const tripAdvisorRatingItem = getTripAdvisorRating(property.customerRatings);
  const hasInclusions = inclusions.length > 0;
  const propertyImageSrcSet = property.mainImage
    ? `${get(property, 'mainImage.urlLarge')} 2x, ${get(property, 'mainImage.urlMedium')} 1x`
    : undefined;
  const imageAltTag = `${property.name} - ${get(property, 'address.suburb')} - ${get(property, 'address.country')}`;
  const total = charges.total;
  const hasDiscount = charges.totalBeforeDiscount && charges.totalBeforeDiscount.amount !== charges.total.amount;
  const totalBeforeDiscount = charges.totalBeforeDiscount;
  const promotionSash = get(offer, 'promotion.name');
  const numberOfNights = getNumberOfNights({ checkIn, checkOut });
  const isClassic = offer.type === 'classic';
  const isCurrencyCash = total.currency !== 'PTS';
  const ariaCurrency = total.currency === 'PTS' ? 'points' : 'dollars';

  return (
    <Flex flex="1 1 auto" justifyContent="space-between" flexDirection="row">
      <Box flexBasis={theme.imageSizes.searchThumbnail.width}>
        <Image
          lazy
          width={theme.imageSizes.searchThumbnail.width}
          height={theme.imageSizes.searchThumbnail.height}
          src={get(property, 'mainImage.urlMedium')}
          srcSet={propertyImageSrcSet}
          alt={imageAltTag}
          borderRadius="defaultRoundLeftOnly"
        />
      </Box>

      <Flex flex="1 1 auto" flexDirection={['column', 'row']}>
        <ResultDetailsContainer>
          {promotionSash && (
            <PromotionalSashContainer>
              <PromotionalSash promotionName={promotionSash} data-testid="promotional-sash" fontSize="xs" />
            </PromotionalSashContainer>
          )}

          <SearchResultLink
            propertyId={property.id}
            cardIndex={cardIndex}
            ariaLabel={`${property.name} from ${parseInt(total.amount)} ${ariaCurrency}`}
          >
            <Truncate lines={2}>
              <Heading.h2 color="greys.charcoal" mb={0} fontSize={['sm', 'lg']} data-testid="property-name" lineHeight="normal">
                {property.name}
              </Heading.h2>
            </Truncate>
          </SearchResultLink>

          <Heading.h3
            display={['none', 'block']}
            color="greys.dusty"
            fontSize="sm"
            mb={1}
            fontWeight="normal"
            data-testid="property-suburb"
          >
            {property?.address?.suburb}
          </Heading.h3>

          <Flex mt={1} alignItems="center" flexDirection="row">
            <StarRating rating={property.rating} ratingType={property.ratingType} size={12} />
            {tripAdvisorRatingItem && <TripAdvisorRating rating={tripAdvisorRatingItem} small displayReviews={!isMobile} ml={[2, 4]} />}
          </Flex>

          {!isMobile && hasInclusions && <Inclusions inclusions={inclusions} mt="auto" alignItems="center" />}
        </ResultDetailsContainer>
        <Flex p={3} pl={[3, 5, 0]} pt={[1, 8, 8]} flex="1 1 auto" flexDirection="column" alignItems="flex-end">
          <Text display="block" color="greys.charcoal" lineHeight="30px" fontSize={['xs', 'sm']} data-testid="number-of-nights">
            {numberOfNights} from{isCurrencyCash && ` (${total.currency})`}
          </Text>
          {hasDiscount && (
            <PriceBeforeDiscount
              total={totalBeforeDiscount}
              hideCurrency={isCurrencyCash}
              fontSize={['sm', 'base']}
              lineHeight="0.6"
              roundToCeiling
            />
          )}
          <Flex alignItems="center">
            {isClassic && <Icon ml="-6px" name="ribbon" size={28} data-testid="classic-ribbon" color="brand.primary" />}
            <Currency
              amount={total.amount}
              currency={total.currency}
              roundToCeiling
              fontSize={['lg', '3xl']}
              hideCurrency={isCurrencyCash}
              color="brand.primary"
              fontWeight="bold"
            />
          </Flex>
          <CampaignPriceMessage currency={total.currency} offerType={offer.type} country={country} />
          <PointsEarnSummary {...offer.pointsEarned.qffPoints} fontSize={['xs', 'sm']} display="block" mt={1} />
          <CancellationRefundSummary
            cancellationPolicy={cancellationPolicy}
            fontSize={['xs', 'sm']}
            hideBeforeDate
            hideWhenNonRefundable
            display="block"
          />
        </Flex>
      </Flex>
    </Flex>
  );
});

SearchResultDetail.displayName = 'SearchResultDetail';

SearchResultDetail.propTypes = {
  result: PropTypes.shape({
    property: PropTypes.object.isRequired,
    offer: PropTypes.object.isRequired,
  }).isRequired,
  cardIndex: PropTypes.number.isRequired,
};

export default SearchResultDetail;
